import { of, from, Observable, concat } from 'rxjs';
import { mergeMap, map, catchError, withLatestFrom, filter } from 'rxjs/operators';
import { combineEpics, ofType } from 'redux-observable';
import * as R from 'ramda';
import * as Promise from 'bluebird';
import {
  spectreappReady,
  spectreappSetUserSession,
  spectreappGetLastCustomerInfo,
  spectreappSetLastCustomerInfo,
  spectreappLoginUser,
  spectreappStartConnected,
  spectreappGetImageDirectory,
  spectreappSetImageDirectory,
  spectreappSetIsDesktop,
  spectreappDatabaseSync,
  spectreappDatabaseSynced,
  deviceUnsubscribed,
  spectreappCurrentPage,
  types,
} from './spectreApp.redux';
import {
  syncDeviceLocalStart,
  syncDeviceLocalProgress,
  syncDeviceLocalEnd,
  syncDeviceRemoteStart,
  syncDeviceRemoteProgress,
  syncDeviceRemoteEnd,
} from 'components/syncDeviceModal/syncDeviceModal.redux';
import { types as bowlersTypes } from 'components/bowlers/bowlers.redux';
import { types as specsheetTypes } from 'components/specsheet/specsheet.redux';
import { types as arsenalTypes } from 'components/arsenal/arsenal.redux';
import { apiStatus } from 'components/api/api.redux';
import uuid from 'uuid/v1';
import { now, encryptSession, decryptSession, deleteSession, getDatabaseName } from '../../utils';
import CryptoJS from 'crypto-js';
import TripleDES from 'crypto-js/tripledes';
import { openSQLiteDatabase, executeSql } from '../db/database';
import { getAppConfig } from '../../appConfig';
import { datasources, initDatasources } from '../db/datasources';
import { fileSystem, isDesktop, getPlatform } from 'components/fileSystem';
import { createSpecsheet } from 'components/specsheet/createSpecsheet';
import { api } from 'components/api';

const setupLocalSession = () => {
  const data = {
    id: uuid(),
    username: '',
    firstname: '',
    lastname: '',
    password: '',
    proshops: [
      {
        proshop: {
          id: uuid(),
          name: '',
        },
      },
    ],
  };

  const userSession = {
    username: '',
    rememberMe: true,
    loginSince: now(),
    access_token: null,
    user_data: data,
    user_database: `spectre_dbuser_${data.id.replace(/-/g, '')}`,
    last_user: {
      last_user: data.username,
      last_password: TripleDES.encrypt(data.password, data.id).toString(),
      last_login: now(),
      last_proshop: data.proshops[0].proshop.id,
      api_token: null,
      refresh_token: null,
      expires_in: null,
      last_token_refresh: now(),
    },
    currentProshop: data.proshops[0].proshop.id,
    checkOfflineOn: false,
  };

  encryptSession(userSession, data.id);

  return userSession;
};

const getLocaleStorageUserSession = () => {
  const decryptedSession = decryptSession();
  console.log(decryptedSession);
  if (decryptedSession) {
    return {
      userSession: decryptedSession,
      sessionId: decryptedSession.user_data.id,
    };
  } else {
    const userSession = setupLocalSession();
    return {
      userSession,
      sessionId: userSession.user_data.id,
    };
  }
};

const setupLocalAppUser = localSessionData => {
  const { appUserDS, appUserProshopDS, proshopDS } = datasources;

  return R.pipeP(
    appUserDS.get,
    currentAppUser => {
      if (!currentAppUser) {
        const appUser = {
          localId: localSessionData.id,
          name: localSessionData.username,
          firstname: localSessionData.firstname,
          lastname: localSessionData.lastname,
          password: localSessionData.password,
        };

        return appUserDS.create(appUser);
      }

      return Promise.resolve();
    },
    () => proshopDS.get(localSessionData.proshops[0].proshop.id),
    currentProshop => {
      if (!currentProshop) {
        const proshop = {
          localId: localSessionData.proshops[0].proshop.id,
          name: '',
        };
        return proshopDS.create(proshop);
      }

      return Promise.resolve();
    },
    () =>
      appUserProshopDS.read({
        proshop_id: localSessionData.proshops[0].proshop.id,
      }),
    appUserProshops => {
      const currentAppUserProshops = R.filter(appUserProshop => appUserProshop.user_id === localSessionData.id)(
        appUserProshops
      );
      if (R.length(currentAppUserProshops) === 0) {
        const appUserProshop = {
          user_id: localSessionData.id,
          proshop_id: localSessionData.proshops[0].proshop.id,
        };
        return appUserProshopDS.create(appUserProshop);
      }

      return Promise.resolve();
    }
  )(localSessionData.id);
};

const userLogin = syncActionsObserver => ({ userSession, userData }) =>
  new Promise((success, reject) => {
    var last_password = TripleDES.decrypt(userSession.last_user.last_password, userSession.user_data.id).toString(
      CryptoJS.enc.Utf8
    );

    if (userData.password === last_password) {
      spectreappLoginUser({
        rememberMe: userData.rememberMe,
      });

      encryptSession(userSession, userSession.user_data.id);

      openSQLiteDatabase({
        alias: 'spectre_db',
        databaseName: userSession.user_database,
        databaseVersion: getAppConfig().databaseVersion,
        databaseDescription: 'spectreDB',
      }).then(() => {
        console.log('DATABASE OPEN!!!!!');
        const syncActions = {
          localSyncStart: () => syncActionsObserver.next(syncDeviceLocalStart()),
          localSyncProgress: progress => syncActionsObserver.next(syncDeviceLocalProgress({ progress })),
          localSyncEnd: () => syncActionsObserver.next(syncDeviceLocalEnd()),
          remoteSyncStart: () => syncActionsObserver.next(syncDeviceRemoteStart()),
          remoteSyncProgress: progress => syncActionsObserver.next(syncDeviceRemoteProgress({ progress })),
          remoteSyncEnd: () => syncActionsObserver.next(syncDeviceRemoteEnd()),
        };

        const fixDBExecuteSql = executeSql('spectre_db');
        const proshopId = userSession.user_data.proshops[0].proshop.id;

        const updateQueue = [];

        updateQueue.push({ sql: 'UPDATE app_user SET id=?', params: [userSession.user_data.id] });
        updateQueue.push({ sql: 'UPDATE proshop SET id=?', params: [proshopId] });
        updateQueue.push({
          sql: 'UPDATE app_user_proshop SET user_id=?, proshop_id=?',
          params: [userData.id, proshopId],
        });
        updateQueue.push({
          sql: 'UPDATE customer_proshop SET proshop_id=?',
          params: [proshopId],
        });

        fixDBExecuteSql({ queue: updateQueue }).then(() =>
          initDatasources('spectre_db', userSession.user_data.id, syncActions).then(() => {
            console.info('Datasources initalized.');
            setupLocalAppUser(userSession.user_data)
              .then(userPayload => {
                syncActionsObserver.next(spectreappDatabaseSync());
                success(userPayload);
              })
              .catch(error => {
                console.warn(`User login: ${error}`);
                reject(error);
              });
          })
        );
      });
    } else {
      // No login will open the login windows
      reject(`Can't login user`);
    }
  });

const getDeviceInfo = userSession => {
  const DeviceUUID = require('device-uuid').DeviceUUID;
  const deviceUUID = new DeviceUUID();
  const deviceId = deviceUUID.get();

  return R.pipe(
    R.pathOr([], ['user_data', 'devices']),
    R.find(R.propEq('device_id', deviceId))
  )(userSession);
};

const spectreappApplicationStartEpic = action$ =>
  action$.pipe(
    ofType(types.spectreappApplicationStart),
    mergeMap(() =>
      Observable.create(spectreappApplicationStartObserver => {
        const { userSession, sessionId } = getLocaleStorageUserSession();
        const { setAccessToken, setApiActions, ajaxSession, setUserSessionUserData } = api;
        const checkOfflineOn = userSession.checkOfflineOn || false;

        const getUser = () =>
          new Promise((success, fail) => {
            console.log('Get logged User');

            ajaxSession(`user`, { type: 'GET' })
              .then(user_data => {
                success(user_data);
              })
              .catch(fail);
          });

        setApiActions({
          connectionStatus: status => spectreappApplicationStartObserver.next(apiStatus(status)),
        });

        if (checkOfflineOn) {
          const username = userSession.username;
          const password = TripleDES.decrypt(userSession.last_user.last_password, sessionId).toString(
            CryptoJS.enc.Utf8
          );

          setAccessToken({
            access_token: null,
            user_data: userSession.user_data,
            autologinCredentials: { username, password },
            checkOfflineOn: true,
          });

          getUser()
            .then(user_data => {
              setUserSessionUserData(user_data);
              userSession.user_data = user_data;

              const subscriptionInfo =
                user_data.subscription && user_data.subscription !== ''
                  ? JSON.parse(user_data.subscription.subscription_info || '{}')
                  : {};

              const subscriptionStatus = subscriptionInfo.status || 'NO_INFO';

              spectreappApplicationStartObserver.next(
                spectreappSetUserSession({
                  userSession,
                  sessionId,
                  deviceInfo: getDeviceInfo(userSession),
                  subscriptionStatus,
                })
              );
            })
            .catch(error => {
              console.log('GET USER ERROR:', error);
              spectreappApplicationStartObserver.next(
                spectreappSetUserSession({
                  userSession,
                  sessionId,
                  deviceInfo: getDeviceInfo(userSession),
                  subscriptionStatus: 'NO_INFO',
                })
              );
            });
        } else {
          spectreappApplicationStartObserver.next(
            spectreappSetUserSession({
              userSession,
              sessionId,
              deviceInfo: getDeviceInfo(userSession),
              subscriptionStatus: 'NO_INFO',
            })
          );
        }
      })
    )
  );

const spectreappDatabaseConnectedEpic = action$ =>
  action$.pipe(
    ofType(types.spectreappSetUserSession),
    mergeMap(({ payload: { userSession } }) => {
      return of(spectreappStartConnected({ databaseName: userSession.user_database }));
    })
  );

const spectreappSetUserSessionEpic = action$ =>
  action$.pipe(
    ofType(types.spectreappSetUserSession),
    mergeMap(({ payload: { userSession, sessionId } }) =>
      Observable.create(syncActionsObserver => {
        const { copyEmailBck } = fileSystem();

        const loggingInUser = R.pipeP(
          userLogin(syncActionsObserver),
          () => copyEmailBck('en'),
          () => copyEmailBck('fr'),
          () => copyEmailBck('es')
        );

        loggingInUser({
          userSession: userSession,
          userData: {
            username: userSession.username,
            password: CryptoJS.TripleDES.decrypt(userSession.last_user.last_password, sessionId).toString(
              CryptoJS.enc.Utf8
            ),
          },
        })
          .then(() => {
            syncActionsObserver.next(spectreappGetLastCustomerInfo());
          })
          .catch(error => {
            console.warn(error);
            syncActionsObserver.next(spectreappGetLastCustomerInfo());
          });
      })
    )
  );

const spectreappSelectArsenalEpic = (action$, state$) =>
  action$.pipe(
    ofType(arsenalTypes.ballSelect),
    filter(action => !!action.payload.ball),
    withLatestFrom(state$),
    mergeMap(([{ payload: { ball } }, state]) => {
      const lastCustomerInfo = state.spectreApp.lastCustomerInfo;
      const newLastCustomerInfo = {
        ...lastCustomerInfo,
        [lastCustomerInfo.lastCustomer]: { ...lastCustomerInfo[lastCustomerInfo.lastCustomer], lastArsenal: ball.id },
      };
      window.localStorage.setItem('spectre_lastcustomerInfo', JSON.stringify(newLastCustomerInfo));
      return of(spectreappSetLastCustomerInfo({ lastCustomerInfo: newLastCustomerInfo }));
    })
  );

const spectreappSelectSpecsheetEpic = (action$, state$) =>
  action$.pipe(
    ofType(specsheetTypes.specsheetSelect),
    filter(action => !!action.payload.specsheet),
    withLatestFrom(state$),
    mergeMap(([{ payload: { specsheet } }, state]) => {
      const lastCustomerInfo = state.spectreApp.lastCustomerInfo;
      const newLastCustomerInfo = {
        ...lastCustomerInfo,
        [lastCustomerInfo.lastCustomer]: {
          ...lastCustomerInfo[lastCustomerInfo.lastCustomer],
          lastSpecSheet: specsheet.id,
        },
      };
      window.localStorage.setItem('spectre_lastcustomerInfo', JSON.stringify(newLastCustomerInfo));
      return of(spectreappSetLastCustomerInfo({ lastCustomerInfo: newLastCustomerInfo }));
    })
  );

const spectreappGetLastCustomerInfoEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.spectreappGetLastCustomerInfo),
    withLatestFrom(state$),
    mergeMap(([, state]) =>
      from(
        new Promise(success => {
          const spectreLastCustomerInfo = window.localStorage.getItem('spectre_lastcustomerInfo');
          const { customerDS, customerProshopDS, specSheetDS, ballDrilledDS } = datasources;
          const appSettings = state.settings.appSettings;

          const createFirstCustomerInfo = () =>
            new Promise(createFirstCustomerInfoSuccess => {
              const firstCustomerId = uuid();
              const firstSpecsheetId = uuid();
              const firstArsenalId = uuid();

              customerDS
                .create({ localId: firstCustomerId })
                .then(() =>
                  customerProshopDS.create({
                    customer_id: firstCustomerId,
                    proshop_id: state$.value.spectreApp.userSession.currentProshop,
                  })
                )
                .then(() =>
                  specSheetDS.create(
                    createSpecsheet(appSettings)({
                      localId: firstSpecsheetId,
                      customerId: firstCustomerId,
                    })
                  )
                )
                .then(() =>
                  ballDrilledDS.create({
                    localId: firstArsenalId,
                    customer_id: firstCustomerId,
                  })
                )
                .then(() => {
                  const lastCustomerInfo = {
                    lastCustomer: firstCustomerId,
                  };
                  lastCustomerInfo[firstCustomerId] = {
                    lastSpecSheet: firstSpecsheetId,
                    lastArsenal: firstArsenalId,
                  };
                  createFirstCustomerInfoSuccess(lastCustomerInfo);
                })
                .catch(error => console.warn(error));
            });

          const checkSpecSheets = customerId =>
            new Promise(success => {
              specSheetDS.read({ customerId: lastCustomerInfo.lastCustomer }).then(specsheets => {
                if (specsheets.length > 0) {
                  success(specsheets[0].id);
                } else {
                  const firstSpecsheetId = uuid();

                  specSheetDS
                    .create(
                      createSpecsheet(appSettings)({
                        localId: firstSpecsheetId,
                        customerId: customerId,
                      })
                    )
                    .then(() => success(firstSpecsheetId));
                }
              });
            });

          const checkArsenals = customerId =>
            new Promise(success => {
              ballDrilledDS.read({ customerId: lastCustomerInfo.lastCustomer }).then(arsenals => {
                if (arsenals.length > 0) {
                  success(arsenals[0].id);
                } else {
                  const firstArsenalId = uuid();

                  ballDrilledDS
                    .create({
                      localId: firstArsenalId,
                      customer_id: customerId,
                    })
                    .then(() => success(firstArsenalId));
                }
              });
            });

          const recreateCustomerInfo = () => {
            customerDS.read({}).then(customers => {
              if (customers.length > 0) {
                const lastCustomer = customers[0].id;
                const lastCustomerInfo = { lastCustomer: lastCustomer };
                lastCustomerInfo[lastCustomer] = {};
                Promise.all([checkSpecSheets(lastCustomer), checkArsenals(lastCustomer)]).then(
                  ([lastSpecSheet, lastArsenal]) => {
                    lastCustomerInfo[lastCustomer].lastSpecSheet = lastSpecSheet;
                    lastCustomerInfo[lastCustomer].lastArsenal = lastArsenal;
                    window.localStorage.setItem('spectre_lastcustomerInfo', JSON.stringify(lastCustomerInfo));
                    success(lastCustomerInfo);
                  }
                );
              } else {
                createFirstCustomerInfo().then(lastCustomerInfo => {
                  window.localStorage.setItem('spectre_lastcustomerInfo', JSON.stringify(lastCustomerInfo));
                  success(lastCustomerInfo);
                });
              }
            });
          };

          if (spectreLastCustomerInfo) {
            const lastCustomerInfoObj = JSON.parse(spectreLastCustomerInfo);
            const lastCustomer = lastCustomerInfoObj.lastCustomer;
            if (!lastCustomer) {
              recreateCustomerInfo();
            } else {
              success(lastCustomerInfoObj);
            }
          } else {
            recreateCustomerInfo();
          }
        })
      ).pipe(
        map(lastCustomerInfo => {
          return spectreappSetLastCustomerInfo({ lastCustomerInfo });
        }),
        catchError(error => {
          console.warn(error);
          return spectreappSetLastCustomerInfo({ lastCustomerInfo: {} });
        })
      )
    )
  );

const spectreappSelectCustomerEpic = (action$, state$) =>
  action$.pipe(
    ofType(bowlersTypes.bowlerSelect),
    withLatestFrom(state$),
    mergeMap(([{ payload: { bowler } }, state]) =>
      from(
        new Promise(success => {
          const { specSheetDS, ballDrilledDS } = datasources;
          const appSettings = state.settings.appSettings;

          const checkSpecSheets = customerId =>
            new Promise(success => {
              specSheetDS.read({ customerId: customerId }).then(specsheets => {
                if (specsheets.length > 0) {
                  success(specsheets[0].id);
                } else {
                  const firstSpecsheetId = uuid();

                  specSheetDS
                    .create(
                      createSpecsheet(appSettings)({
                        localId: firstSpecsheetId,
                        customerId: customerId,
                      })
                    )
                    .then(() => success(firstSpecsheetId));
                }
              });
            });

          const checkArsenals = customerId =>
            new Promise(success => {
              ballDrilledDS.read({ customerId: customerId }).then(arsenals => {
                if (arsenals.length > 0) {
                  success(arsenals[0].id);
                } else {
                  const firstArsenalId = uuid();

                  ballDrilledDS
                    .create({
                      localId: firstArsenalId,
                      customer_id: customerId,
                    })
                    .then(() => success(firstArsenalId));
                }
              });
            });
          if (bowler && bowler.id) {
            const lastCustomer = bowler.id;
            const lastCustomerInfo = { lastCustomer: lastCustomer };
            lastCustomerInfo[lastCustomer] = {};
            Promise.all([checkSpecSheets(lastCustomer), checkArsenals(lastCustomer)]).then(
              ([lastSpecSheet, lastArsenal]) => {
                lastCustomerInfo[lastCustomer].lastSpecSheet = lastSpecSheet;
                lastCustomerInfo[lastCustomer].lastArsenal = lastArsenal;
                window.localStorage.setItem('spectre_lastcustomerInfo', JSON.stringify(lastCustomerInfo));
                success(lastCustomerInfo);
              }
            );
          } else {
            window.localStorage.setItem('spectre_lastcustomerInfo', JSON.stringify({}));
            success({});
          }
        })
      ).pipe(
        map(lastCustomerInfo => {
          return spectreappSetLastCustomerInfo({ lastCustomerInfo });
        }),
        catchError(error => {
          console.warn(error);
          return spectreappSetLastCustomerInfo({ lastCustomerInfo: {} });
        })
      )
    )
  );

const spectreappSetLastCustomerInfoEpic = action$ =>
  action$.pipe(
    ofType(types.spectreappSetLastCustomerInfo),
    mergeMap(() => {
      return of(spectreappGetImageDirectory());
    })
  );

const spectreappGetImageDirectoryUrlEpic = action$ =>
  action$.pipe(
    ofType(types.spectreappGetImageDirectory),
    mergeMap(() => {
      const { getImageDirectory } = fileSystem();

      return from(getImageDirectory()).pipe(
        map(imageDirectory => {
          // const imageDirectoryReader = imageDirectory.createReader();
          if (imageDirectory && imageDirectory.nativeURL) {
            return spectreappSetImageDirectory({
              imageDirectory: imageDirectory.nativeURL,
            });
          } else if (imageDirectory && imageDirectory.localURL) {
            return spectreappSetImageDirectory({
              imageDirectory: imageDirectory.localURL,
            });
          } else {
            return spectreappSetImageDirectory({
              imageDirectory: '',
            });
          }
        })
      );
    })
  );

const spectreappSetImageDirectoryInfoEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.spectreappSetLastCustomerInfo),
    withLatestFrom(state$),
    mergeMap(([, state]) => {
      if (!state.spectreApp.ready) {
        navigator.splashscreen.hide(); //cordova

        const newIpads = ['iPad8,1', 'iPad8,2', 'iPad8,3', 'iPad8,4', 'iPad8,5', 'iPad8,6', 'iPad8,7', 'iPad8,8'];

        if (newIpads.indexOf(device.model) >= 0) {
          window.StatusBar.overlaysWebView(true);
        }
        return of(spectreappReady());
      } else {
        return of();
      }
    })
  );

const spectreappSetIsDesktopEpic = action$ =>
  action$.pipe(
    ofType(types.spectreappReady),
    mergeMap(() =>
      of(
        spectreappSetIsDesktop({
          isDesktop: isDesktop(),
          platform: getPlatform(),
        })
      )
    )
  );

const spectreappDatabaseSyncEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.spectreappDatabaseSync),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) =>
      Observable.create(syncActionsObserver => {
        const syncDatabase = () =>
          new Promise((success, fail) => {
            console.info('Syncing Datasources');
            // setUserSessionForceLocalSync(true);

            R.pipeP(
              datasources.customerDS.sync,
              datasources.customerProshopDS.sync,
              datasources.specSheetDS.sync,
              datasources.thumbCutDS.sync,
              // datasources.thumbInfoDS.sync,
              datasources.ballDrilledDS.sync
            )()
              .then(() => {
                // setUserSessionForceLocalSync(false);
                success();
              })
              .catch(fail);
          });

        if (state.spectreApp.subscriptionStatus === 'ACTIVE') {
          const timeoutTime = (state.settings.appSettings.auto_sync_interval || 5 * 60 * 1000) * 1;
          setTimeout(() => {
            syncActionsObserver.next(spectreappDatabaseSync());
          }, timeoutTime);

          syncDatabase()
            .then(() => {
              syncActionsObserver.next(spectreappDatabaseSynced());
            })
            .catch(error => {
              console.warn(error);
              syncActionsObserver.next(spectreappDatabaseSynced());
            });
        }
      })
    )
  );

const unsubscribeDevice = ({ deviceId }) =>
  new Promise((success, fail) => {
    console.log('unSubscribe device');
    const { ajaxSession } = api;

    ajaxSession(`userDevice/${deviceId}`, {
      type: 'DELETE',
      data: {},
    })
      .then(success)
      .catch(fail);
  });

const unsubscribeDeviceEpic = action$ =>
  action$.pipe(
    ofType(types.deviceUnsubscribe),
    mergeMap(({ payload }) => {
      if (!payload.unlinkDevice) {
        const decryptedSession = decryptSession();
        console.log(decryptedSession);

        decryptedSession.user_data.devices = [];
        decryptedSession.checkOfflineOn = false;
        decryptedSession.access_token = null;

        encryptSession(decryptedSession, decryptedSession.user_data.id);
        return concat(
          of(spectreappCurrentPage({ currentPage: 'loginForm' })),
          of(deviceUnsubscribed({ userSession: decryptedSession, sessionId: decryptedSession.user_data.id }))
        );
      } else {
        return from(unsubscribeDevice(payload)).pipe(
          map(() => {
            deleteSession();

            setTimeout(() => {
              location.reload();
            }, 500);

            return of(deviceUnsubscribed({ userSession: {}, sessionId: null }));
          })
        );
      }
    })
  );

export default combineEpics(
  spectreappApplicationStartEpic,
  spectreappSetUserSessionEpic,
  spectreappDatabaseConnectedEpic,
  spectreappGetLastCustomerInfoEpic,
  spectreappSetLastCustomerInfoEpic,
  spectreappSelectCustomerEpic,
  spectreappGetImageDirectoryUrlEpic,
  spectreappSetImageDirectoryInfoEpic,
  spectreappSelectArsenalEpic,
  spectreappSetIsDesktopEpic,
  spectreappSelectSpecsheetEpic,
  spectreappDatabaseSyncEpic,
  unsubscribeDeviceEpic
);
